import { useState } from 'react';
import { getSurveySettings, renderSurveyMessage } from './leadActivity';

const Stars = ({ value, onChange, size = 34, readOnly }) => (
  <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} onClick={() => !readOnly && onChange && onChange(n)}
        style={{ fontSize: size, cursor: readOnly ? 'default' : 'pointer', color: n <= value ? '#f5a623' : '#d9d9d9', lineHeight: 1 }}>★</span>
    ))}
  </div>
);

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const card = { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, maxHeight: '92vh', overflowY: 'auto', padding: 22, fontFamily: 'inherit' };
const btn = (bg) => ({ padding: '10px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' });

export function FeedbackSurveyModal({ appointment, onSend, onResponse, onClose }) {
  const a = appointment;
  const [step, setStep] = useState(a.feedback ? 'done' : a.survey_sent ? 'customer' : 'ready');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [err, setErr] = useState('');
  const name = a.lead_name || 'Customer';
  const link = 'https://vbot.co.in/feedback/' + String(a.id).slice(-6);

  const send = () => { onSend(a.id); setStep('customer'); };
  const submit = () => {
    if (!rating) { setErr('Please select a rating'); return; }
    onResponse(a.id, rating, comment.trim());
    setStep('done');
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>📋 Feedback survey</div>
          <span onClick={onClose} style={{ cursor: 'pointer', fontSize: 22, color: '#999', lineHeight: 1 }}>×</span>
        </div>

        {step === 'ready' && (
          <>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 10 }}>Appointment completed. This WhatsApp message will be sent to the customer:</div>
            <div style={{ background: '#DCF8C6', borderRadius: 10, padding: 12, fontSize: 13, color: '#222', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderSurveyMessage(getSurveySettings().message, { name, product: a.product || '', link })}
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>To: {a.phone || 'no phone saved'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ ...btn('#eee'), color: '#555' }}>Later</button>
              <button onClick={send} style={btn('#25d366')}>Send survey (preview mode)</button>
            </div>
          </>
        )}

        {step === 'customer' && (
          <>
            <div style={{ background: '#E8F5E9', color: '#2e7d32', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, marginBottom: 14 }}>
              ✅ Survey sent (preview mode). The customer will see this:
            </div>
            <div style={{ background: '#f4f5fa', borderRadius: 14, padding: 18, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e', marginBottom: 12 }}>How was your visit, {name}?</div>
              <Stars value={rating} onChange={(n) => { setRating(n); setErr(''); }} />
              <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Any comments? (optional)"
                style={{ width: '100%', boxSizing: 'border-box', minHeight: 70, border: '1px solid #ddd', borderRadius: 8, padding: 10, marginTop: 12, fontFamily: 'inherit', fontSize: 13 }} />
              {err && <div style={{ color: '#d33', fontSize: 12, marginTop: 6 }}>{err}</div>}
              <button onClick={submit} style={{ ...btn('#25d366'), marginTop: 12 }}>Submit as customer (demo)</button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: 40 }}>🙏</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e', margin: '6px 0 10px' }}>Feedback received</div>
            <Stars value={(a.feedback && a.feedback.rating) || rating} readOnly size={28} />
            {((a.feedback && a.feedback.comment) || comment) && (
              <div style={{ fontSize: 13, color: '#555', marginTop: 10, fontStyle: 'italic' }}>"{(a.feedback && a.feedback.comment) || comment}"</div>
            )}
            <button onClick={onClose} style={{ ...btn('#4F46E5'), marginTop: 16 }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function FeedbackSummary({ appointments }) {
  const sent = appointments.filter(a => a.survey_sent);
  const rated = appointments.filter(a => a.feedback);
  const avg = rated.length ? (rated.reduce((s, a) => s + a.feedback.rating, 0) / rated.length) : 0;
  const rate = sent.length ? Math.round((rated.length / sent.length) * 100) : 0;
  const dist = [5, 4, 3, 2, 1].map(n => ({ n, c: rated.filter(a => a.feedback.rating === n).length }));
  const latest = rated.slice(-3).reverse();

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e', marginBottom: 10 }}>⭐ Customer feedback</div>
      {sent.length === 0 ? (
        <div style={{ fontSize: 12, color: '#aaa' }}>Survey not sent yet. Mark the appointment as Completed to send the survey.</div>
      ) : (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 110 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#f5a623', lineHeight: 1 }}>{rated.length ? avg.toFixed(1) : '–'}</div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{sent.length} sent · {rated.length} replied · {rate}%</div>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            {dist.map(d => (
              <div key={d.n} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#777', marginBottom: 3 }}>
                <span style={{ width: 14 }}>{d.n}★</span>
                <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 6 }}>
                  <div style={{ width: (rated.length ? (d.c / rated.length) * 100 : 0) + '%', background: '#f5a623', height: 6, borderRadius: 4 }} />
                </div>
                <span style={{ width: 14, textAlign: 'right' }}>{d.c}</span>
              </div>
            ))}
          </div>
          {latest.length > 0 && (
            <div style={{ flexBasis: '100%' }}>
              {latest.map(a => (
                <div key={a.id} style={{ fontSize: 12, color: '#555', borderTop: '1px solid #f3f3f3', padding: '6px 0' }}>
                  <b>{a.lead_name}</b> · <span style={{ color: '#f5a623' }}>{'★'.repeat(a.feedback.rating)}</span>
                  {a.feedback.comment ? ' · "' + a.feedback.comment + '"' : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
